/* global Office */

/**
 * officeItemHelpers
 * Thin Promise wrappers around Office.js compose-item APIs.
 * All functions include dev-mode fallbacks for when Office.js is not available.
 */

const isDev = () =>
  typeof Office === "undefined" || !Office.context?.mailbox?.item;

// ─── Recipients ─────────────────────────────────────────────────────────────

/**
 * Collect To + Cc recipients from the active compose item (merged).
 * @returns {Promise<Array<{ emailAddress: string, displayName: string }>>}
 */
export async function getRecipients() {
  if (isDev()) return [];

  const item = Office.context.mailbox.item;
  const getAsync = (field) =>
    new Promise((resolve) => {
      field.getAsync((result) => {
        resolve(
          result.status === Office.AsyncResultStatus.Succeeded
            ? result.value
            : [],
        );
      });
    });

  const [toList, ccList] = await Promise.all([
    getAsync(item.to),
    getAsync(item.cc),
  ]);
  return [...toList, ...ccList];
}

/**
 * Collect To and Cc recipients separately.
 * @returns {Promise<{ to: Array<{ emailAddress: string, displayName: string }>, cc: Array<{ emailAddress: string, displayName: string }> }>}
 */
export async function getRecipientsWithCc() {
  if (isDev()) return { to: [], cc: [] };

  const item = Office.context.mailbox.item;
  const getAsync = (field) =>
    new Promise((resolve) => {
      field.getAsync((result) => {
        resolve(
          result.status === Office.AsyncResultStatus.Succeeded
            ? result.value
            : [],
        );
      });
    });

  const [toList, ccList] = await Promise.all([
    getAsync(item.to),
    getAsync(item.cc),
  ]);
  return { to: toList, cc: ccList };
}

// ─── Subject ─────────────────────────────────────────────────────────────────

/**
 * Get the current compose item subject.
 * @returns {Promise<string>}
 */
export async function getSubject() {
  if (isDev()) return "Test-Betreff (Dev)";

  return new Promise((resolve) => {
    Office.context.mailbox.item.subject.getAsync((result) => {
      resolve(
        result.status === Office.AsyncResultStatus.Succeeded
          ? result.value ?? ""
          : "",
      );
    });
  });
}

/**
 * Set the compose item subject.
 * @param {string} subject
 */
export async function setSubject(subject) {
  if (isDev()) {
    console.log("[DEV] setSubject:", subject);
    return;
  }

  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.subject.setAsync(subject, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve();
      } else {
        reject(new Error(result.error?.message ?? "setSubject failed"));
      }
    });
  });
}

// ─── Body ────────────────────────────────────────────────────────────────────

/**
 * Get the compose item body as HTML.
 * @returns {Promise<string>}
 */
export async function getBody() {
  if (isDev()) return "<p>Dev mode body.</p>";

  return new Promise((resolve) => {
    Office.context.mailbox.item.body.getAsync(
      Office.CoercionType.Html,
      (result) => {
        resolve(
          result.status === Office.AsyncResultStatus.Succeeded
            ? result.value ?? ""
            : "",
        );
      },
    );
  });
}

/**
 * Set the compose item body (HTML).
 * @param {string} html
 */
export async function setBody(html) {
  if (isDev()) {
    console.log("[DEV] setBody (length):", html.length);
    return;
  }

  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.body.setAsync(
      html,
      { coercionType: Office.CoercionType.Html },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(new Error(result.error?.message ?? "setBody failed"));
        }
      },
    );
  });
}

// ─── Attachments ─────────────────────────────────────────────────────────────

/**
 * Get all attachments on the current compose item.
 * @returns {Promise<Array<{ id: string, name: string, isInline: boolean, size: number, attachmentType: string }>>}
 */
export async function getAttachmentItems() {
  if (isDev()) return [];

  return new Promise((resolve) => {
    Office.context.mailbox.item.getAttachmentsAsync((result) => {
      resolve(
        result.status === Office.AsyncResultStatus.Succeeded
          ? result.value ?? []
          : [],
      );
    });
  });
}

/**
 * Fetch the base64 content of a single attachment.
 * @param {string} attachmentId
 * @returns {Promise<{ content: string }>} content is base64
 */
export async function getAttachmentContent(attachmentId) {
  if (isDev()) return { content: "" };

  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.getAttachmentContentAsync(
      attachmentId,
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value); // { content: base64string, format: "base64" }
        } else {
          reject(
            new Error(
              result.error?.message ?? "getAttachmentContent failed",
            ),
          );
        }
      },
    );
  });
}

/**
 * Remove an attachment by id.
 * @param {string} attachmentId
 */
export async function removeAttachment(attachmentId) {
  if (isDev()) {
    console.log("[DEV] removeAttachment:", attachmentId);
    return;
  }

  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.removeAttachmentAsync(
      attachmentId,
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(
            new Error(result.error?.message ?? "removeAttachment failed"),
          );
        }
      },
    );
  });
}

/**
 * Add a base64-encoded file as an attachment to the compose item.
 * @param {string} base64 - base64-encoded file content
 * @param {string} fileName
 * @param {boolean} isInline - false for regular file attachments
 */
export async function addBase64Attachment(base64, fileName, isInline = false) {
  if (isDev()) {
    console.log("[DEV] addBase64Attachment:", fileName, `(${base64.length} chars base64)`);
    return;
  }

  return new Promise((resolve, reject) => {
    Office.context.mailbox.item.addFileAttachmentFromBase64Async(
      base64,
      fileName,
      { isInline },
      (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve();
        } else {
          reject(
            new Error(
              result.error?.message ?? "addBase64Attachment failed",
            ),
          );
        }
      },
    );
  });
}

// ─── User info ────────────────────────────────────────────────────────────────


/**
 * Returns the current user's email address from the Office profile.
 * @returns {string}
 */
export function getUserEmail() {
  if (isDev()) return "dev@example.com";
  return Office.context.mailbox?.userProfile?.emailAddress ?? "";
}

// ─── EML ─────────────────────────────────────────────────────────────────────

/**
 * Builds a minimal RFC 822 EML from the current compose item and returns it
 * as a base64-encoded string. The Office.js compose API does not expose raw
 * EML, so we reconstruct it from the available fields (subject, body, recipients,
 * attachments). Returns null in dev mode or on failure.
 *
 * @returns {Promise<string|null>}
 */
export async function getEmlBase64() {
  if (isDev()) return null;

  try {
    const [subject, bodyHtml, { to, cc }, allItems] = await Promise.all([
      getSubject(),
      getBody(),
      getRecipientsWithCc(),
      getAttachmentItems(),
    ]);

    const boundary = "----=_Part_" + Math.random().toString(36).slice(2);
    const now = new Date().toUTCString();

    const formatAddresses = (list) =>
      list.map((r) =>
        r.displayName ? `"${r.displayName}" <${r.emailAddress}>` : r.emailAddress
      ).join(", ");

    const senderEmail = Office.context.mailbox.userProfile.emailAddress;
    const senderName = Office.context.mailbox.userProfile.displayName;
    const fromHeader = senderName ? `"${senderName}" <${senderEmail}>` : senderEmail;

    // ── Headers ──
    let eml = "";
    eml += `Date: ${now}\r\n`;
    eml += `From: ${fromHeader}\r\n`;
    eml += `Subject: ${subject}\r\n`;
    if (to.length > 0)  eml += `To: ${formatAddresses(to)}\r\n`;
    if (cc.length > 0)  eml += `Cc: ${formatAddresses(cc)}\r\n`;
    eml += `MIME-Version: 1.0\r\n`;

    const nonInlineAttachments = allItems.filter((a) => !a.isInline);

    if (nonInlineAttachments.length === 0) {
      // Simple case: no attachments — text/html body only
      eml += `Content-Type: text/html; charset="utf-8"\r\n`;
      eml += `Content-Transfer-Encoding: base64\r\n`;
      eml += `\r\n`;
      eml += btoa(unescape(encodeURIComponent(bodyHtml)));
    } else {
      // Multipart: body + attachments
      eml += `Content-Type: multipart/mixed; boundary="${boundary}"\r\n`;
      eml += `\r\n`;

      // Body part
      eml += `--${boundary}\r\n`;
      eml += `Content-Type: text/html; charset="utf-8"\r\n`;
      eml += `Content-Transfer-Encoding: base64\r\n`;
      eml += `\r\n`;
      eml += btoa(unescape(encodeURIComponent(bodyHtml)));
      eml += `\r\n`;

      // Attachment parts
      for (const item of nonInlineAttachments) {
        try {
          const { content } = await getAttachmentContent(item.id);
          eml += `--${boundary}\r\n`;
          eml += `Content-Type: application/octet-stream; name="${item.name}"\r\n`;
          eml += `Content-Transfer-Encoding: base64\r\n`;
          eml += `Content-Disposition: attachment; filename="${item.name}"\r\n`;
          eml += `\r\n`;
          eml += content; // already base64 from getAttachmentContent
          eml += `\r\n`;
        } catch (_) {
          // soft-fail: skip this attachment
        }
      }

      eml += `--${boundary}--\r\n`;
    }

    return btoa(unescape(encodeURIComponent(eml)));
  } catch (err) {
    console.warn("[getEmlBase64] Failed to build EML:", err);
    return null;
  }
}

// ─── Email Snapshot ───────────────────────────────────────────────────────────

/**
 * Captures an immutable snapshot of the current compose item BEFORE any
 * mutation (subject/body replacement, attachment removal) takes place.
 * Used by Phase 4 to store the original unencrypted copy in Sent Items.
 *
 * @returns {Promise<{ subject: string, bodyHtml: string, recipients: Array, attachments: Array<{ fileName: string, base64: string }> }>}
 */
export async function captureEmailSnapshot() {
  if (isDev()) {
    return {
      subject: "Test-Betreff (Dev)",
      bodyHtml: "<p>Dev mode body.</p>",
      recipients: [],
      attachments: [],
    };
  }

  const [subject, bodyHtml, recipients, allItems] = await Promise.all([
    getSubject(),
    getBody(),
    getRecipients(),
    getAttachmentItems(),
  ]);

  const attachments = [];
  for (const item of allItems.filter((a) => !a.isInline)) {
    try {
      const { content } = await getAttachmentContent(item.id);
      attachments.push({ fileName: item.name, base64: content });
    } catch (_) {
      // soft-fail: omit this attachment from the snapshot
    }
  }

  return { subject, bodyHtml, recipients, attachments };
}

// ─── Sent Items Copy ──────────────────────────────────────────────────────────

/**
 * Saves an unencrypted copy of the original email to the user's Sent Items
 * folder using EWS CreateItem. The subject is prefixed with a marker so the
 * user can distinguish the copy from regular sent emails.
 *
 * This function is called AFTER saveCompose() — the compose item is already
 * gone at this point. EWS is the only Office.js mechanism available here.
 *
 * Throws on failure — callers must treat this as a soft-fail and show a
 * warning rather than marking the overall send as failed.
 *
 * @param {{ subject: string, bodyHtml: string, recipients: Array<{ emailAddress: string, displayName: string }>, attachments: Array<{ fileName: string, base64: string }> }} snapshot
 */
export async function saveUnencryptedCopyToSentItems(snapshot) {
  // Cannot use isDev() here — after saveCompose() the compose item is gone,
  // so Office.context.mailbox.item is null and isDev() would return true even in prod.
  // Instead check for EWS availability directly.
  const ewsAvailable =
    typeof Office !== "undefined" &&
    typeof Office.context?.mailbox?.makeEwsRequestAsync === "function";

  console.log("[EWS] saveUnencryptedCopyToSentItems called:", {
    officeAvailable: typeof Office !== "undefined",
    mailboxAvailable: !!Office?.context?.mailbox,
    ewsAvailable,
    itemAvailable: !!Office?.context?.mailbox?.item,
    subject: snapshot?.subject,
  });

  if (!ewsAvailable) {
    if (typeof Office === "undefined" || !Office.context?.mailbox) {
      console.log("[EWS] Office not available — dev mode, skipping.");
      return;
    }
    throw new Error("EWS not available in this Outlook context");
  }

  const sentSubject =
    "[Verschlüsselt versendet mit Avamboo Encrypt] " + snapshot.subject;

  // Build <ToRecipients> XML
  const toRecipientsXml = snapshot.recipients
    .filter((r) => r.emailAddress)
    .map(
      (r) =>
        `<t:Mailbox><t:EmailAddress>${_escapeXml(r.emailAddress)}</t:EmailAddress></t:Mailbox>`,
    )
    .join("");

  const createItemSoap = `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
               xmlns:xsd="http://www.w3.org/2001/XMLSchema"
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"
               xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types"
               xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages">
  <soap:Header>
    <t:RequestServerVersion Version="Exchange2013" />
  </soap:Header>
  <soap:Body>
    <m:CreateItem MessageDisposition="SaveOnly">
      <m:SavedItemFolderId>
        <t:DistinguishedFolderId Id="sentitems" />
      </m:SavedItemFolderId>
      <m:Items>
        <t:Message>
          <t:Subject>${_escapeXml(sentSubject)}</t:Subject>
          <t:Body BodyType="HTML">${_escapeXml(snapshot.bodyHtml)}</t:Body>
          ${toRecipientsXml ? `<t:ToRecipients>${toRecipientsXml}</t:ToRecipients>` : ""}
        </t:Message>
      </m:Items>
    </m:CreateItem>
  </soap:Body>
</soap:Envelope>`;

  const createResult = await new Promise((resolve, reject) => {
    Office.context.mailbox.makeEwsRequestAsync(createItemSoap, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message ?? "EWS CreateItem failed"));
      }
    });
  });

  console.log("[EWS] CreateItem response received:", createResult?.substring(0, 200));

  // Parse ItemId from response for attachment calls
  const itemIdMatch = createResult.match(/ItemId Id="([^"]+)" ChangeKey="([^"]+)"/);
  if (!itemIdMatch) {
    throw new Error("EWS CreateItem: could not parse ItemId from response");
  }
  const itemId = itemIdMatch[1];
  const changeKey = itemIdMatch[2];

  // Attach original attachments (best-effort, one at a time)
  for (const att of snapshot.attachments) {
    try {
      const createAttSoap = `<?xml version="1.0" encoding="utf-8"?>
<soap:Envelope xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
               xmlns:xsd="http://www.w3.org/2001/XMLSchema"
               xmlns:soap="http://schemas.xmlsoap.org/soap/envelope/"
               xmlns:t="http://schemas.microsoft.com/exchange/services/2006/types"
               xmlns:m="http://schemas.microsoft.com/exchange/services/2006/messages">
  <soap:Header>
    <t:RequestServerVersion Version="Exchange2013" />
  </soap:Header>
  <soap:Body>
    <m:CreateAttachment>
      <m:ParentItemId Id="${_escapeXml(itemId)}" ChangeKey="${_escapeXml(changeKey)}" />
      <m:Attachments>
        <t:FileAttachment>
          <t:Name>${_escapeXml(att.fileName)}</t:Name>
          <t:Content>${att.base64}</t:Content>
        </t:FileAttachment>
      </m:Attachments>
    </m:CreateAttachment>
  </soap:Body>
</soap:Envelope>`;

      await new Promise((resolve, reject) => {
        Office.context.mailbox.makeEwsRequestAsync(createAttSoap, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve();
          } else {
            reject(new Error(result.error?.message ?? "EWS CreateAttachment failed"));
          }
        });
      });
    } catch (attErr) {
      // soft-fail per attachment — the Sent Items copy still exists without this file
      console.warn("[officeItemHelpers] CreateAttachment soft-fail:", att.fileName, attErr);
    }
  }
}

/** Minimal XML character escaping for EWS payloads. */
function _escapeXml(str) {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// ─── Save ─────────────────────────────────────────────────────────────────────

/**
 * Persists all mutations to the compose item (subject, body, attachments).
 * The compose window stays open — the user sends manually via Outlook's Send button.
 */
export async function saveCompose() {
  if (isDev()) {
    console.log("[DEV] saveCompose() called — changes saved to compose item (compose stays open in dev)");
    return;
  }

  await new Promise((resolve, reject) => {
    Office.context.mailbox.item.saveAsync((result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve();
      } else {
        reject(new Error(result.error?.message ?? "saveAsync failed"));
      }
    });
  });
}
